// Reliability.tsx — what is derivable today, and an honest account of the rest.
//
// DERIVABLE NOW: AI provider reliability from GET /usage/latency, which returns
// calls / errors / rerouted / skipped / p50 / p95 / max per provider+model from
// the raw ai_usage_events rows. That is real telemetry and it is shown as such.
//
// NOT DERIVABLE: HTTP uptime, API p50/p95, incidents, and job-queue depth. None
// of them is recorded anywhere. The design shows all four; this screen names each
// and says what would have to be captured. It does not render a zero, because on
// a reliability screen a zero reads as "healthy" and the truth is "unmeasured".
//
// One caveat stated on screen: /usage/latency reads ai_usage_events, which is
// only written when CONSOLE_USAGE_ROLLUP_ENABLED-era telemetry is flowing. With
// the emitter live but no traffic, the table is legitimately empty — so an empty
// result is reported as "no calls recorded", never as "0ms latency".

import React, { useCallback, useEffect, useState } from "react";
import { PT, PT_NUM, fmtNum, fmtMs, rateColor } from "../../platformTheme";
import { pget, perr } from "./api";
import { PCard, PSection, PBtn, PBanner, PEmpty, PSpinner, PTable, PRow, PStat, PGrid, th, td, tdNum } from "./ui";
import { NotCaptured } from "./charts";

interface LatencyRow {
  tenant_id?: string | null; tenant_name?: string | null;
  provider: string; provider_display_name?: string;
  model: string; calls: string; errors: string;
  rerouted: string; skipped: string;
  p50_ms: number | null; p95_ms: number | null; max_ms: number | null;
}
interface SilentProvider { provider: string; provider_display_name: string; is_active?: boolean }
interface FailedTenant { tenant_id: string | null; tenant_name: string; error: string }

const tenantLabel = (r: { tenant_id?: string | null; tenant_name?: string | null }): string =>
  r.tenant_name || (r.tenant_id ? r.tenant_id.slice(0, 8) : "Platform / unattributed");

export const Reliability: React.FC<{ days: number }> = ({ days }) => {
  const [rows, setRows] = useState<LatencyRow[] | null>(null);
  const [silent, setSilent] = useState<SilentProvider[]>([]);
  const [failed, setFailed] = useState<FailedTenant[]>([]);
  const [err, setErr] = useState("");
  // Refresh reports its own state. Without it a re-read over unchanged rows is
  // indistinguishable from a button that does nothing, which is what the
  // operator reported.
  const [busy, setBusy] = useState(false);
  const [at, setAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setErr(""); setBusy(true);
    const r = await pget("/usage/latency", { days });
    setBusy(false); setAt(new Date());
    if (!r.ok) { setErr(perr(r, "Could not load provider reliability")); setRows([]); return; }
    setRows(r.data?.rows || []);
    setSilent(r.data?.providers_without_usage || []);
    setFailed(r.data?.tenants_failed || []);
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const totals = (rows || []).reduce(
    (a, r) => ({
      calls: a.calls + Number(r.calls || 0),
      errors: a.errors + Number(r.errors || 0),
      rerouted: a.rerouted + Number(r.rerouted || 0),
      skipped: a.skipped + Number(r.skipped || 0),
    }),
    { calls: 0, errors: 0, rerouted: 0, skipped: 0 },
  );
  const errPct = totals.calls ? (totals.errors / totals.calls) * 100 : null;

  return (
    <>
      <PBanner>{err}</PBanner>

      {failed.length > 0 && (
        <PBanner>
          {failed.length} customer{failed.length > 1 ? "s" : ""} could not be read
          ({failed.map((f) => f.tenant_name).join(", ")}) — the figures below are partial.
        </PBanner>
      )}

      <PSection
        title="AI provider reliability"
        sub={`every customer · ${days}-day window${at ? ` · updated ${at.toLocaleTimeString()}` : ""}`}
        right={<PBtn small disabled={busy} onClick={load}>{busy ? "Refreshing…" : "Refresh"}</PBtn>}
      >
        <PGrid min={175}>
          <PStat label="Calls" value={rows === null ? "…" : fmtNum(totals.calls)} sub="all providers" />
          <PStat
            label="Error rate"
            value={errPct == null ? "—" : `${errPct.toFixed(2)}%`}
            sub={totals.calls ? `${fmtNum(totals.errors)} of ${fmtNum(totals.calls)}` : "no calls recorded"}
          />
          <PStat label="Rerouted" value={rows === null ? "…" : fmtNum(totals.rerouted)} sub="fell through to a fallback tier" />
          <PStat label="Skipped" value={rows === null ? "…" : fmtNum(totals.skipped)} sub="tier skipped by rule" />
        </PGrid>

        <div style={{ marginTop: 14 }}>
          <PCard style={{ padding: 0 }}>
            {rows === null ? <PSpinner /> : rows.length === 0 ? (
              <PEmpty>
                No AI calls recorded in this window. The usage emitter writes on every routed call;
                an empty table means no traffic, not zero latency.
              </PEmpty>
            ) : (
              <PTable
                minWidth={760}
                head={<>
                  <th style={th}>Customer</th>
                  <th style={th}>Provider</th><th style={th}>Model</th>
                  <th style={{ ...th, textAlign: "right" }}>Calls</th>
                  <th style={{ ...th, textAlign: "right" }}>Errors</th>
                  <th style={{ ...th, textAlign: "right" }}>p50</th>
                  <th style={{ ...th, textAlign: "right" }}>p95</th>
                  <th style={{ ...th, textAlign: "right" }}>Max</th>
                </>}
              >
                {rows.map((r) => {
                  const calls = Number(r.calls || 0), errors = Number(r.errors || 0);
                  return (
                    <PRow key={`${r.tenant_id || "none"}:${r.provider}:${r.model}`}>
                      <td style={{ ...td, fontWeight: 600 }}>{tenantLabel(r)}</td>
                      <td style={td}>{r.provider_display_name || r.provider}</td>
                      <td style={{ ...td, fontFamily: PT.fontMono, fontSize: 11.5 }}>{r.model}</td>
                      <td style={tdNum}>{fmtNum(calls)}</td>
                      <td style={{ ...tdNum, color: rateColor(errors, calls) }}>{fmtNum(errors)}</td>
                      <td style={tdNum}>{fmtMs(r.p50_ms)}</td>
                      <td style={tdNum}>{fmtMs(r.p95_ms)}</td>
                      <td style={tdNum}>{fmtMs(r.max_ms)}</td>
                    </PRow>
                  );
                })}
              </PTable>
            )}
          </PCard>
        </div>

        {/* A configured provider with zero calls is a fact, not an absence. */}
        {silent.length > 0 && (
          <PCard style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 5 }}>
              Configured providers with no calls in this window
            </div>
            <div style={{ fontSize: 12, color: PT.txM, marginBottom: 9 }}>
              In the routing catalog, nothing routed to them in the last {days} days. That is a
              routing observation, not a configuration gap.
            </div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
              {silent.map((p) => (
                <span key={p.provider} style={{
                  fontSize: 11.5, padding: "3px 9px", borderRadius: 7,
                  border: `1px solid ${PT.brd}`, background: PT.bg3, color: PT.txM,
                }}>
                  {p.provider_display_name} · 0 calls{p.is_active === false ? " · inactive" : ""}
                </span>
              ))}
            </div>
          </PCard>
        )}
      </PSection>

      {/* 2026-07-28. Two of the four entries that used to sit here have been
          REMOVED because they were wrong, and the panels that replace them are
          rendered below by CoreMetrics:

            * "Job-queue depth, running, failed, oldest" said reporting depth
              "requires either a real queue (pg-boss/BullMQ) or a jobs table the
              workers claim from". The second one exists — extraction_jobs, with
              a CHECK-constrained status and created/started/completed stamps.

            * "Per-customer SLA" read SLA as infrastructure uptime. The product
              models a TURNAROUND SLA: migration 059 put sla_target_date,
              sla_severity, sla_breached and sla_breached_at on
              contract_requests, and that is the SLA an operator reviews with a
              customer.

          The remaining two are genuinely uncaptured and stay stated. They are
          rendered by CoreMetrics' Availability panel rather than duplicated
          here, so there is ONE place that says what is not measured. */}
      <PSection title="Not captured yet" sub="What the design shows that the platform does not record">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12 }}>
          <NotCaptured
            what="API uptime and HTTP p50/p95"
            needs="Request-timing middleware on the Express app plus a rollup. The AI latency above is per model call, not per HTTP request — they are different numbers and one cannot stand in for the other."
          />
          <NotCaptured
            what="Infrastructure incident timeline"
            needs="An incidents table, or an integration with a status page / on-call tool. Neither exists. The SLA breach timeline below is a product record, not an outage record — showing them together would misrepresent both."
          />
        </div>
      </PSection>
    </>
  );
};
